import React, { useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import {
  MAX_PHOTOS,
  MIN_PHOTOS,
  PHOTO_REQUIREMENTS_EN,
  catalogLabel,
} from "./constants";

const PREVIEW_COLUMNS = [
  { key: "rowNumber", label: "#" },
  { key: "mobileNumber", label: "Mobile" },
  { key: "title", label: "Title" },
  { key: "cityEn", label: "City" },
  { key: "propertyType", label: "Property type" },
  { key: "basePrice", label: "Base price" },
];

const PreviewPhotos = ({
  listings,
  apiRows,
  catalogs,
  photosByRow,
  isSubmitting,
  onAddPhotos,
  onRemovePhoto,
  onSetCover,
  onCreate,
  onBack,
}) => {
  const photoInputRefs = useRef({});

  const typeLabel = (id) =>
    catalogLabel((catalogs.propertyTypes || []).find((p) => p.id === id)) ||
    id ||
    "—";

  const allPhotosReady = listings.every((_, index) => {
    const rowNumber = apiRows[index]?.rowNumber;
    const count = photosByRow[rowNumber]?.length || 0;
    return count >= MIN_PHOTOS && count <= MAX_PHOTOS;
  });

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">
            Preview: {listings.length} listing
            {listings.length === 1 ? "" : "s"}
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Values were validated. Attach {MIN_PHOTOS}–{MAX_PHOTOS} photos per
            listing, then create and request approval.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="rounded-md border border-gray-400 px-4 py-2 text-sm disabled:opacity-50"
        >
          ← Edit the form
        </button>
      </div>

      <div className="overflow-x-auto rounded-md border border-gray-400 dark:border-white">
        <table className="w-full min-w-[640px] border-collapse text-center text-sm">
          <thead>
            <tr>
              {PREVIEW_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="border-b border-gray-500 px-3 py-2 font-semibold dark:border-white"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {listings.map((listing, index) => {
              const row = apiRows[index];
              return (
                <tr key={listing.id}>
                  <td className="border-b border-gray-500 px-3 py-2 dark:border-white">
                    {row.rowNumber}
                  </td>
                  <td className="border-b border-gray-500 px-3 py-2 dark:border-white">
                    {row.mobileNumber}
                  </td>
                  <td className="max-w-[200px] break-words border-b border-gray-500 px-3 py-2 dark:border-white">
                    {row.title}
                  </td>
                  <td className="border-b border-gray-500 px-3 py-2 dark:border-white">
                    {row.cityEn}
                  </td>
                  <td className="border-b border-gray-500 px-3 py-2 dark:border-white">
                    {typeLabel(row.propertyType)}
                  </td>
                  <td className="border-b border-gray-500 px-3 py-2 dark:border-white">
                    {row.basePrice} SAR
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-darkBg2 dark:text-gray-300">
        <p>{PHOTO_REQUIREMENTS_EN}</p>
        <p className="mt-2">
          Accepted formats: JPG, PNG, or WebP. The first photo is the cover.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {listings.map((listing, index) => {
          const rowNumber = apiRows[index].rowNumber;
          const photos = photosByRow[rowNumber] || [];
          const ready =
            photos.length >= MIN_PHOTOS && photos.length <= MAX_PHOTOS;
          return (
            <div
              key={`photos-${listing.id}`}
              className="rounded-md border border-gray-400 p-4 dark:border-white"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium">
                    {listing.title || "Untitled listing"}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {photos.length} / {MAX_PHOTOS} photos
                    {!ready && (
                      <span className="text-amber-700">
                        {" "}
                        — need at least {MIN_PHOTOS}
                      </span>
                    )}
                    {ready && (
                      <span className="text-green-700"> — ready</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={isSubmitting || photos.length >= MAX_PHOTOS}
                  onClick={() => photoInputRefs.current[rowNumber]?.click()}
                  className="flex items-center gap-2 rounded-md border border-secondaryColor px-3 py-2 text-secondaryColor disabled:opacity-50"
                >
                  <ImagePlus size={16} />
                  Add photos
                </button>
                <input
                  ref={(node) => {
                    photoInputRefs.current[rowNumber] = node;
                  }}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(event) => onAddPhotos(rowNumber, event)}
                />
              </div>
              {photos.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {photos.map((photo, photoIndex) => (
                    <div
                      key={photo.id}
                      className="relative h-24 w-24 overflow-hidden rounded-md border border-gray-300 dark:border-gray-600"
                    >
                      <img
                        src={photo.previewUrl}
                        alt={`${listing.title || "Listing"} ${photoIndex + 1}`}
                        className="h-full w-full object-cover"
                      />
                      {photoIndex === 0 && (
                        <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] text-white">
                          Cover
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1">
                        {photoIndex !== 0 && (
                          <button
                            type="button"
                            className="text-[10px] text-white"
                            onClick={() => onSetCover(rowNumber, photo.id)}
                          >
                            Cover
                          </button>
                        )}
                        <button
                          type="button"
                          className="ml-auto text-white"
                          aria-label="Remove photo"
                          onClick={() => onRemovePhoto(rowNumber, photo.id)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onCreate}
          disabled={isSubmitting || !allPhotosReady}
          className="rounded-md bg-secondaryColor px-4 py-2 text-white disabled:opacity-50"
        >
          {isSubmitting
            ? "Creating listings..."
            : `Create and request approval for ${listings.length} listing${listings.length === 1 ? "" : "s"}`}
        </button>
        {!allPhotosReady && (
          <p className="self-center text-sm text-amber-700">
            Attach {MIN_PHOTOS}–{MAX_PHOTOS} photos for every listing before
            creating.
          </p>
        )}
      </div>
    </section>
  );
};

export default PreviewPhotos;
